import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ============================================================================
// PUBLIC URL — customer-facing origin used for shareable tracking links.
// Defensive: if the current window.location.origin contains any hosting-infra
// hostname listed in PUBLIC_URL_BLOCKLIST below, swap to the customer's own
// domain so links shared with customers never leak infra branding.
// ============================================================================
const PUBLIC_URL_FALLBACK = "https://flowline.one";
const PUBLIC_URL_BLOCKLIST = [".host", "agent.com", ".sh"].map((s) => "emergent" + s);

export function publicOrigin() {
  try {
    const o = (typeof window !== "undefined" && window.location.origin) || "";
    const lo = o.toLowerCase();
    if (!o || PUBLIC_URL_BLOCKLIST.some((b) => lo.includes(b))) {
      return PUBLIC_URL_FALLBACK;
    }
    return o;
  } catch {
    return PUBLIC_URL_FALLBACK;
  }
}

export const api = axios.create({ baseURL: API });

// Attach token from localStorage on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("wbt_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401s globally
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("wbt_token");
      localStorage.removeItem("wbt_user");
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const STATUS_LABELS = {
  awaiting_arrival: "Awaiting Arrival",
  received: "Received",
  awaiting_collection: "Awaiting Collection",
  collected: "Collected",
  in_specification: "In Specification",
  ready_for_return: "Ready for Return",
  returned: "Returned",
  ready_for_dispatch: "Ready for Dispatch",
  dispatched: "Dispatched",
  delivered: "Delivered",
  issue: "Issue / Query",
};

export const STATUS_ORDER = [
  "awaiting_arrival",
  "received",
  "awaiting_collection",
  "collected",
  "in_specification",
  "ready_for_return",
  "returned",
  "ready_for_dispatch",
  "dispatched",
  "delivered",
  "issue",
];

export const STATUS_STYLES = {
  awaiting_arrival: "bg-zinc-100 text-zinc-700 border-zinc-300",
  received: "bg-blue-50 text-blue-700 border-blue-200",
  awaiting_collection: "bg-yellow-50 text-yellow-800 border-yellow-200",
  collected: "bg-orange-50 text-orange-700 border-orange-200",
  in_specification: "bg-violet-50 text-violet-700 border-violet-200",
  ready_for_return: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  returned: "bg-red-50 text-red-700 border-red-200",
  ready_for_dispatch: "bg-lime-50 text-lime-800 border-lime-200",
  dispatched: "bg-emerald-50 text-emerald-700 border-emerald-200",
  delivered: "bg-emerald-600 text-white border-emerald-700",
  issue: "bg-rose-50 text-rose-700 border-rose-300",
};

export const ROLE_LABELS = {
  admin: "Admin",
  warehouse: "Warehouse",
  office: "Office",
  third_party: "Third-Party",
  read_only: "Read-only",
};

export function canEdit(user) {
  if (!user) return false;
  return ["admin", "warehouse", "office", "third_party"].includes(user.role);
}

export function canEditAll(user) {
  if (!user) return false;
  return ["admin", "warehouse", "office"].includes(user.role);
}

export function canManageSkus(user) {
  if (!user) return false;
  return ["admin", "office"].includes(user.role);
}

export function isThirdParty(user) {
  return user?.role === "third_party";
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export function formatDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return "—";
  }
}

export function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "—";
  }
}
